/**
 * The entitlement gates' vocabulary — per capability, its group refs, template tag and gated action ids.
 *
 * One of the package's concerns alongside `github.ts` and `desktop.ts`; `index.ts` is a pure barrel.
 */

/**
 * Entra group "Backstage AVD Users", as normalised into the catalog by the microsoftGraphOrg
 * provider (display name → lower-case with underscores).
 */
export const AVD_GROUP_REF = "group:default/backstage_avd_users";

/** Entra group "Backstage Admins" — the platform team; it opens every entitlement gate. */
export const PLATFORM_ADMIN_GROUP_REF = "group:default/backstage_admins";

/**
 * Every group ref that entitles a user to AVD. Matched against `ownershipEntityRefs`, which
 * carries the user's own ref plus every group they belong to.
 */
export const AVD_ENTITLED_GROUP_REFS: readonly string[] = [AVD_GROUP_REF, PLATFORM_ADMIN_GROUP_REF];

/**
 * Tag carried by the AVD scaffolder templates, in `metadata.tags` and in `backstage:permissions.tags`
 * on every parameter page and step. The policy hides the template on the former and each page or
 * step on the latter, so an untagged page or step is shown to everyone. Enforced by `packages/backend/src/templateContract.test.ts`.
 */
export const AVD_TEMPLATE_TAG = "avd";

/** Scaffolder action id that provisions the signed-in user's desktop. */
export const AVD_PROVISION_ACTION_ID = "avd:provision";

/** Scaffolder action id that tears the signed-in user's desktop down. */
export const AVD_DEPROVISION_ACTION_ID = "avd:deprovision";

/**
 * Both AVD lifecycle actions. The permission policy denies execution of every id in this list to
 * non-entitled users — deprovision is destructive, so it is gated exactly as tightly as provision.
 */
export const AVD_GATED_ACTION_IDS: readonly string[] = [
	AVD_PROVISION_ACTION_ID,
	AVD_DEPROVISION_ACTION_ID,
];

/** Entra group "Backstage SWA Users", as the microsoftGraphOrg provider normalises it into the catalog. */
export const SWA_GROUP_REF = "group:default/backstage_swa_users";

/** Every group ref that entitles a user to request a web app bundle. */
export const SWA_ENTITLED_GROUP_REFS: readonly string[] = [SWA_GROUP_REF, PLATFORM_ADMIN_GROUP_REF];

/** Tag carried by the SWA templates, in the same two places as {@link AVD_TEMPLATE_TAG}. */
export const SWA_TEMPLATE_TAG = "swa";

/** Scaffolder action id that requests a web app bundle for the signed-in user. */
export const SWA_PROVISION_ACTION_ID = "swa:provision";

/** The scaffolder action that deletes one web app bundle; gated like provisioning. */
export const SWA_DELETE_ACTION_ID = "swa:delete";

/** The SWA actions the policy denies to non-members; each is namespaced `swa:`. */
export const SWA_GATED_ACTION_IDS: readonly string[] = [
	SWA_PROVISION_ACTION_ID,
	SWA_DELETE_ACTION_ID,
];

/**
 * One capability a curated group unlocks (ADR-0011): its template tag, which is also its action-id
 * namespace, the groups that grant it, and the Entra display name `hosting-group-membership.yaml` offers.
 */
export type EntitlementGate = {
	tag: string;
	groupDisplayName: string;
	groupRefs: readonly string[];
	actionIds: readonly string[];
};

/** The AVD desktop gate. */
export const AVD_GATE: EntitlementGate = {
	tag: AVD_TEMPLATE_TAG,
	groupDisplayName: "Backstage AVD Users",
	groupRefs: AVD_ENTITLED_GROUP_REFS,
	actionIds: AVD_GATED_ACTION_IDS,
};

/** The web app (SWA) bundle gate. */
export const SWA_GATE: EntitlementGate = {
	tag: SWA_TEMPLATE_TAG,
	groupDisplayName: "Backstage SWA Users",
	groupRefs: SWA_ENTITLED_GROUP_REFS,
	actionIds: SWA_GATED_ACTION_IDS,
};

/** Every entitlement gate. The policy, the frontend and the contract tests iterate this list. */
export const ENTITLEMENT_GATES: readonly EntitlementGate[] = [AVD_GATE, SWA_GATE];

/** The gates a set of ownership refs does not open: none of the gate's groups is among them. */
export function lackedGates(ownershipEntityRefs: readonly string[]): EntitlementGate[] {
	return ENTITLEMENT_GATES.filter(
		(gate) => !gate.groupRefs.some((ref) => ownershipEntityRefs.includes(ref)),
	);
}
